/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, XCircle, RotateCcw, Send, ArrowLeft, Trophy } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("mgbApprenantToken")}` });
const LETTERS = ["A", "B", "C", "D", "E", "F"];

export const QuizRunner = ({ quizId, onClose, onResult }) => {
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setResult(null);
    setAnswers({});
    axios.get(`${API}/apprenant/quiz/${quizId}`, { headers: authHeaders() })
      .then((r) => setQuiz(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Quiz indisponible"));
  }, [quizId]);

  const submit = async () => {
    if (!quiz) return;
    if (Object.keys(answers).length < quiz.questions.length) {
      toast.error("Répondez à toutes les questions avant de valider");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/apprenant/quiz/${quizId}/submit`, { answers: quiz.questions.map((q) => answers[q.id]) }, { headers: authHeaders() });
      setResult(res.data);
      onResult?.(res.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible");
    } finally {
      setBusy(false);
    }
  };

  if (!quiz) return <p className="text-sm text-gray-500 p-4">Chargement du quiz…</p>;

  if (result) {
    return (
      <Card className={`border-2 ${result.passed ? "border-green-500" : "border-red-300"}`} data-testid="quiz-result">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="text-[#003087] flex items-center gap-2">
                {result.passed ? <Trophy className="h-5 w-5 text-green-600" /> : <XCircle className="h-5 w-5 text-red-500" />}
                {result.passed ? "Quiz réussi !" : "Quiz non validé"}
              </CardTitle>
              <CardDescription>
                Score : <strong data-testid="quiz-score">{result.score}/{result.total}</strong> ({result.pct} %) — seuil de réussite {result.seuil} %
              </CardDescription>
            </div>
            <div className="flex gap-2">
              {!result.passed && (
                <Button variant="outline" onClick={() => { setResult(null); setAnswers({}); }} data-testid="quiz-retry-btn">
                  <RotateCcw className="h-4 w-4 mr-1" /> Réessayer
                </Button>
              )}
              <Button onClick={onClose} className="bg-[#003087] hover:bg-[#002366] text-white" data-testid="quiz-close-btn">
                <ArrowLeft className="h-4 w-4 mr-1" /> Retour au parcours
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {result.correction.map((q, i) => (
            <div key={q.id} className={`p-4 rounded-lg border ${q.correct ? "border-green-200 bg-green-50/50" : "border-red-200 bg-red-50/50"}`} data-testid={`correction-${i}`}>
              <p className="font-semibold text-[#003087] flex items-start gap-2">
                {q.correct ? <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" /> : <XCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />}
                <span>{i + 1}. {q.question}</span>
              </p>
              <ul className="mt-2 space-y-1 text-sm pl-7">
                {q.choices.map((c, ci) => (
                  <li key={ci} className={`flex items-center gap-2 ${ci === q.correct_answer ? "text-green-700 font-semibold" : ci === q.your_answer ? "text-red-600 line-through" : "text-gray-600"}`}>
                    <span className="font-mono text-xs">{LETTERS[ci]}.</span> {c}
                    {ci === q.correct_answer && <Badge className="bg-green-600 text-white text-[10px]">Bonne réponse</Badge>}
                    {ci === q.your_answer && ci !== q.correct_answer && <Badge variant="outline" className="border-red-300 text-red-600 text-[10px]">Votre réponse</Badge>}
                  </li>
                ))}
              </ul>
              {q.explication && <p className="mt-2 pl-7 text-sm text-gray-700 italic">💡 {q.explication}</p>}
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-l-4 border-l-[#F07B1D]" data-testid="quiz-runner">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="text-[#003087]">{quiz.title}</CardTitle>
            <CardDescription>{quiz.questions.length} questions · une seule bonne réponse par question · réussite à partir de {quiz.seuil} %</CardDescription>
          </div>
          <Button variant="ghost" onClick={onClose} data-testid="quiz-cancel-btn"><ArrowLeft className="h-4 w-4 mr-1" /> Annuler</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {quiz.questions.map((q, i) => (
          <fieldset key={q.id} className="p-4 border border-gray-200 rounded-lg bg-white" data-testid={`question-${i}`}>
            <legend className="font-semibold text-[#003087] px-1">{i + 1}. {q.question}</legend>
            <div className="mt-2 grid gap-2">
              {q.choices.map((c, ci) => (
                <label
                  key={ci}
                  className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-colors ${answers[q.id] === ci ? "border-[#F07B1D] bg-[#F07B1D]/10" : "border-gray-200 hover:border-[#2196F3]/60"}`}
                  data-testid={`question-${i}-choice-${ci}`}
                >
                  <input type="radio" name={q.id} className="accent-[#F07B1D]" checked={answers[q.id] === ci} onChange={() => setAnswers({ ...answers, [q.id]: ci })} />
                  <span className="font-mono text-xs text-gray-500">{LETTERS[ci]}.</span>
                  <span className="text-sm text-gray-800">{c}</span>
                </label>
              ))}
            </div>
          </fieldset>
        ))}
        <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
          <p className="text-sm text-gray-500">{Object.keys(answers).length}/{quiz.questions.length} réponses</p>
          <Button onClick={submit} disabled={busy} className="bg-[#F07B1D] hover:bg-[#D96A0F] text-white font-bold" data-testid="quiz-submit-btn">
            <Send className="h-4 w-4 mr-1" /> {busy ? "Correction…" : "Valider mes réponses"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
